import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './RoadmapDinamico.css';
import { demoRoadmapData } from './demoRoadmapData';

/**
 * Demo Roadmap - Versión de demostración pública
 * Usa datos de ejemplo, no persistentes
 * Permite interactuar pero no guarda en localStorage
 */
const DemoRoadmap = () => {
  const [phases, setPhases] = useState([]);
  const [stats, setStats] = useState({
    completed: 0,
    testing: 0,
    inProgress: 0,
    globalProgress: 0,
    totalPhases: 6
  });
  const [collapsedPhases, setCollapsedPhases] = useState(new Set());
  const [selectedTask, setSelectedTask] = useState(null);
  const [checklistState, setChecklistState] = useState({});
  const [viewMode, setViewMode] = useState('list');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDemoBanner, setShowDemoBanner] = useState(true);

  // Inicializar con datos de demo
  useEffect(() => {
    setPhases(demoRoadmapData);
    calculateStats(demoRoadmapData, {});
  }, []);

  // Calcular estadísticas
  const calculateStats = useCallback((phasesData, checklistData) => {
    let totalTasks = 0;
    let completedTasks = 0;
    let testingTasks = 0;
    let inProgressTasks = 0;

    phasesData.forEach(phase => {
      phase.tasks.forEach(task => {
        totalTasks++;

        if (task.checklist && task.checklist.length > 0) {
          const taskChecklist = checklistData[task.id] || {};
          const completedItems = task.checklist.filter(item => taskChecklist[item.id]).length;

          if (completedItems === task.checklist.length) {
            completedTasks++;
          } else if (completedItems > 0) {
            testingTasks++;
          } else if (task.status === 'testing') {
            testingTasks++;
          } else if (task.status === 'progress') {
            inProgressTasks++;
          }
        } else {
          if (task.status === 'completed') completedTasks++;
          if (task.status === 'testing') testingTasks++;
          if (task.status === 'progress') inProgressTasks++;
        }
      });
    });

    const globalProgress = Math.round((completedTasks / totalTasks) * 100);

    setStats({
      completed: completedTasks,
      testing: testingTasks,
      inProgress: inProgressTasks,
      globalProgress: globalProgress,
      totalPhases: phasesData.length
    });
  }, []);

  // Toggle checklist item (solo en memoria)
  const toggleChecklistItem = (taskId, itemId) => {
    const wasCompleted = checklistState[taskId]?.[itemId] || false;
    const newState = {
      ...checklistState,
      [taskId]: {
        ...(checklistState[taskId] || {}),
        [itemId]: !wasCompleted
      }
    };
    setChecklistState(newState);
    calculateStats(phases, newState);
  };

  // Get checklist progress
  const getChecklistProgress = (task) => {
    if (!task.checklist || task.checklist.length === 0) return null;

    const taskChecklist = checklistState[task.id] || {};
    const completed = task.checklist.filter(item => taskChecklist[item.id]).length;
    const total = task.checklist.length;
    const percentage = Math.round((completed / total) * 100);

    return { completed, total, percentage };
  };

  // Get real status based on checklist
  const getRealStatus = (task) => {
    if (!task.checklist || task.checklist.length === 0) {
      return task.status;
    }

    const progress = getChecklistProgress(task);
    if (progress.percentage === 100) return 'completed';
    if (progress.completed > 0) return 'testing';
    if (task.status === 'testing' || task.status === 'completed') return 'testing';
    return task.status;
  };

  // Kanban groups
  const getKanbanGroups = () => {
    const groups = {
      pending: [],
      progress: [],
      testing: [],
      completed: []
    };

    phases.forEach((phase, phaseIndex) => {
      phase.tasks.forEach(task => {
        const status = getRealStatus(task);
        if (groups[status]) {
          groups[status].push({ ...task, phaseIndex, phaseName: phase.name });
        }
      });
    });

    return groups;
  };

  // Cambiar estado de tarea (solo en memoria)
  const changeTaskStatus = (e, phaseIndex, taskId) => {
    e.stopPropagation();
    const updatedPhases = [...phases];
    const task = updatedPhases[phaseIndex].tasks.find(t => t.id === taskId);

    if (task) {
      const statusCycle = ['pending', 'progress', 'testing', 'completed'];
      const currentIndex = statusCycle.indexOf(task.status);
      task.status = statusCycle[(currentIndex + 1) % statusCycle.length];

      setPhases(updatedPhases);
      calculateStats(updatedPhases, checklistState);
    }
  };

  // Calcular progreso de fase
  const calculatePhaseProgress = (phase) => {
    const total = phase.tasks.length;
    let completed = 0;
    let testing = 0;

    phase.tasks.forEach(task => {
      const realStatus = getRealStatus(task);
      if (realStatus === 'completed') completed++;
      if (realStatus === 'testing') testing++;
    });

    const percentage = Math.round((completed / total) * 100);

    return { completed, total, percentage, testing };
  };

  // Toggle colapsar fase
  const togglePhase = (phaseIndex) => {
    const newCollapsed = new Set(collapsedPhases);
    if (newCollapsed.has(phaseIndex)) {
      newCollapsed.delete(phaseIndex);
    } else {
      newCollapsed.add(phaseIndex);
    }
    setCollapsedPhases(newCollapsed);
  };

  // Abrir modal de tarea
  const openTaskModal = (task, phaseIndex) => {
    setSelectedTask({ ...task, phaseIndex });
  };

  // Cerrar modal
  const closeModal = () => {
    setSelectedTask(null);
  };

  // Get status icon
  const getStatusIcon = (task) => {
    const realStatus = getRealStatus(task);
    if (realStatus === 'completed') return '✅';
    if (realStatus === 'testing') return '🧪';
    if (realStatus === 'progress') return '🔄';
    return '⬜';
  };

  // Status badge
  const getStatusBadge = (status, progress = null) => {
    const badges = {
      completed: { icon: '✅', text: 'Completado', class: 'status-completed' },
      testing: { icon: '🧪', text: progress ? `Testing ${progress.percentage}%` : 'Testing', class: 'status-testing' },
      progress: { icon: '🔄', text: 'En Desarrollo', class: 'status-progress' },
      pending: { icon: '📦', text: 'Pendiente', class: 'status-pending' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`status-badge ${badge.class}`}>
        {badge.icon} {badge.text}
      </span>
    );
  };

  return (
    <div className="roadmap-container">
      {/* Animated Background */}
      <div className="god-mode-bg">
        <div className="grid-overlay"></div>
        <div className="glow-orb orb-1"></div>
        <div className="glow-orb orb-2"></div>
        <div className="glow-orb orb-3"></div>
      </div>

      {/* Demo Banner */}
      {showDemoBanner && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(90deg, var(--naranja-boton), var(--marca-600))',
          color: 'white',
          padding: '12px 20px',
          textAlign: 'center',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          fontSize: '0.9rem'
        }}>
          <span>🎯 <strong>Demo interactiva</strong> - Esto es un proyecto de ejemplo. Los cambios no se guardan.</span>
          <Link to="/register" style={{
            background: 'white',
            color: 'var(--naranja-texto)',
            padding: '6px 16px',
            borderRadius: '6px',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '0.85rem'
          }}>
            Crear mi proyecto →
          </Link>
          <button
            onClick={() => setShowDemoBanner(false)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1.2rem',
              padding: '4px'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="roadmap-header" style={{ marginTop: showDemoBanner ? '48px' : 0 }}>
        <div className="header-left">
          <Link to="/" className="btn-back" title="Volver al inicio">
            ← Inicio
          </Link>
          <div className="logo-container">
            <span className="logo-icon">📋</span>
            <div>
              <h1>TASKFLOW DEMO</h1>
            </div>
          </div>
        </div>

        {/* Compact Stats Bar */}
        <div className="stats-bar">
          <div className="stat-mini stat-completed">
            <span className="stat-icon">✅</span>
            <span className="stat-value">{stats.completed}</span>
          </div>
          <div className="stat-mini stat-testing">
            <span className="stat-icon">🧪</span>
            <span className="stat-value">{stats.testing}</span>
          </div>
          <div className="stat-mini stat-progress">
            <span className="stat-icon">🔄</span>
            <span className="stat-value">{stats.inProgress}</span>
          </div>
          <div className="stat-mini stat-global">
            <span className="stat-icon">📊</span>
            <span className="stat-value">{stats.globalProgress}%</span>
          </div>
        </div>

        {/* Hamburger Menu Button */}
        <button
          className={`hamburger-btn ${menuOpen ? 'open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {/* Slide-out Sidebar */}
      {menuOpen && (
        <>
          <div className="sidebar-overlay" onClick={() => setMenuOpen(false)}></div>
          <div className="sidebar-menu">
            <div className="sidebar-header">
              <h3>Menú Demo</h3>
              <button className="sidebar-close" onClick={() => setMenuOpen(false)}>✕</button>
            </div>
            <nav className="sidebar-nav">
              <button
                className="sidebar-item"
                onClick={() => { setViewMode(viewMode === 'list' ? 'kanban' : 'list'); setMenuOpen(false); }}
              >
                <span className="sidebar-icon">{viewMode === 'list' ? '📊' : '📋'}</span>
                <span>{viewMode === 'list' ? 'Vista Kanban' : 'Vista Lista'}</span>
              </button>
              <div className="sidebar-divider"></div>
              <Link to="/register" className="sidebar-item" onClick={() => setMenuOpen(false)}>
                <span className="sidebar-icon">🚀</span>
                <span>Crear mi proyecto</span>
              </Link>
              <Link to="/login" className="sidebar-item" onClick={() => setMenuOpen(false)}>
                <span className="sidebar-icon">🔑</span>
                <span>Iniciar sesión</span>
              </Link>
              <div className="sidebar-divider"></div>
              <Link to="/" className="sidebar-item sidebar-item-muted" onClick={() => setMenuOpen(false)}>
                <span className="sidebar-icon">←</span>
                <span>Volver al inicio</span>
              </Link>
            </nav>
          </div>
        </>
      )}

      {/* Global Progress Bar */}
      <div className="global-progress-container">
        <div className="global-progress-bar">
          <div
            className="global-progress-fill"
            style={{ width: `${stats.globalProgress}%` }}
          >
            <div className="progress-glow"></div>
            <div className="progress-particles"></div>
          </div>
          <div className="progress-markers">
            {[25, 50, 75].map(mark => (
              <div
                key={mark}
                className={`progress-marker ${stats.globalProgress >= mark ? 'passed' : ''}`}
                style={{ left: `${mark}%` }}
              >
                <span>{mark}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="progress-label">
          <span className="progress-text">Progreso Global</span>
          <span className="progress-value">{stats.globalProgress}%</span>
        </div>
      </div>

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <div className="kanban-container">
          {['pending', 'progress', 'testing', 'completed'].map(status => {
            const statusLabels = {
              pending: { icon: '📋', label: 'Pendiente', color: '#64748b' },
              progress: { icon: '🔄', label: 'En Progreso', color: '#3b82f6' },
              testing: { icon: '🧪', label: 'Testing', color: '#fbbf24' },
              completed: { icon: '✅', label: 'Completadas', color: '#22c55e' }
            };
            const tasks = getKanbanGroups()[status];
            const { icon, label, color } = statusLabels[status];

            return (
              <div key={status} className={`kanban-column kanban-${status}`}>
                <div className="kanban-header" style={{ borderColor: color }}>
                  <span className="kanban-icon">{icon}</span>
                  <span className="kanban-title">{label}</span>
                  <span className="kanban-count" style={{ background: color }}>{tasks.length}</span>
                </div>
                <div className="kanban-tasks">
                  {tasks.map(task => (
                    <div
                      key={task.id}
                      className="kanban-task"
                      onClick={() => openTaskModal(task, task.phaseIndex)}
                    >
                      <div className="kanban-task-id">{task.id}</div>
                      <div className="kanban-task-name">{task.name}</div>
                      <div className="kanban-task-phase">{task.phaseName}</div>
                      {task.checklist && (
                        <div className="kanban-task-progress">
                          <div
                            className="kanban-progress-fill"
                            style={{ width: `${getChecklistProgress(task)?.percentage || 0}%` }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                  {tasks.length === 0 && (
                    <div className="kanban-empty">Sin tareas</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Phases (List View) */}
      {viewMode === 'list' && (
        <div className="phases-container">
          {phases.map((phase, phaseIndex) => {
            const progress = calculatePhaseProgress(phase);
            const isCollapsed = collapsedPhases.has(phaseIndex);

            return (
              <div
                key={phaseIndex}
                className={`phase ${isCollapsed ? 'collapsed' : ''} ${progress.percentage === 100 ? 'phase-complete' : ''}`}
              >
                <div
                  className="phase-header"
                  onClick={() => togglePhase(phaseIndex)}
                >
                  <div className="phase-title">
                    <span className={`toggle-icon ${isCollapsed ? 'collapsed' : ''}`}>▼</span>
                    <h2>{phase.name}</h2>
                  </div>
                  <div className="phase-meta">
                    <span className="phase-count">{progress.completed}/{progress.total}</span>
                    {progress.testing > 0 && (
                      <span className="phase-testing-badge">🧪 {progress.testing}</span>
                    )}
                    <div className="phase-progress-wrapper">
                      <div className="phase-progress-bar">
                        <div
                          className="phase-progress-fill"
                          style={{ width: `${progress.percentage}%` }}
                        >
                          <div className="phase-progress-shine"></div>
                        </div>
                      </div>
                      <span className="phase-percentage">{progress.percentage}%</span>
                    </div>
                  </div>
                </div>

                {!isCollapsed && (
                  <div className="phase-content">
                    {phase.tasks.map((task) => {
                      const realStatus = getRealStatus(task);
                      const taskProgress = getChecklistProgress(task);

                      return (
                        <div
                          key={task.id}
                          className={`task status-${realStatus}`}
                          onClick={() => openTaskModal(task, phaseIndex)}
                        >
                          <div className="task-main">
                            <span className="task-id">{task.id}</span>
                            <span className="task-name">{task.name}</span>
                            {task.checklist && (
                              <span className="task-checklist-badge">
                                📋 {taskProgress?.completed || 0}/{task.checklist.length}
                              </span>
                            )}
                          </div>
                          <div className="task-actions">
                            {taskProgress && (
                              <div className="task-mini-progress">
                                <div
                                  className="task-mini-progress-fill"
                                  style={{ width: `${taskProgress.percentage}%` }}
                                />
                              </div>
                            )}
                            {task.tech && (
                              <span className="task-tech">{task.tech.split(',')[0]}</span>
                            )}
                            <button
                              className={`status-btn status-${realStatus}`}
                              onClick={(e) => changeTaskStatus(e, phaseIndex, task.id)}
                            >
                              {getStatusIcon(task)}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="footer">
        <div className="footer-content">
          <span className="footer-logo">📋 TaskFlow Demo</span>
          <span className="footer-version">Roadmap Platform</span>
          <Link to="/register" style={{ color: 'var(--naranja-texto-oscuro)', textDecoration: 'none', fontWeight: '600' }}>
            Crear mi proyecto →
          </Link>
        </div>
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-god-mode" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>

            <div className="modal-header">
              <span className="modal-id">{selectedTask.id}</span>
              <h2>{selectedTask.name}</h2>
              {getStatusBadge(getRealStatus(selectedTask), getChecklistProgress(selectedTask))}
            </div>

            <div className="modal-body">
              {selectedTask.description && (
                <div className="modal-section">
                  <h3>📋 Descripción</h3>
                  <p>{selectedTask.description}</p>
                </div>
              )}

              {/* Checklist */}
              {selectedTask.checklist && selectedTask.checklist.length > 0 && (
                <div className="modal-section checklist-section">
                  <h3>✅ Checklist de Verificación</h3>
                  <div className="checklist-progress">
                    <div className="checklist-progress-bar">
                      <div
                        className="checklist-progress-fill"
                        style={{ width: `${getChecklistProgress(selectedTask)?.percentage || 0}%` }}
                      >
                        <div className="checklist-progress-glow"></div>
                      </div>
                    </div>
                    <span className="checklist-progress-text">
                      {getChecklistProgress(selectedTask)?.completed || 0} / {selectedTask.checklist.length} completados
                    </span>
                  </div>
                  <div className="checklist-items">
                    {selectedTask.checklist.map((item) => {
                      const isChecked = checklistState[selectedTask.id]?.[item.id] || false;
                      return (
                        <div
                          key={item.id}
                          className={`checklist-item ${isChecked ? 'checked' : ''}`}
                          onClick={() => toggleChecklistItem(selectedTask.id, item.id)}
                        >
                          <div className="checklist-checkbox">
                            {isChecked ? '✅' : '⬜'}
                          </div>
                          <span className="checklist-text">{item.text}</span>
                        </div>
                      );
                    })}
                  </div>
                  {getChecklistProgress(selectedTask)?.percentage === 100 && (
                    <div className="checklist-complete-banner">
                      <span>🎉</span>
                      <span>¡Verificación Completa!</span>
                      <span>🎉</span>
                    </div>
                  )}
                </div>
              )}

              {selectedTask.tech && (
                <div className="modal-section">
                  <h3>🛠️ Tecnologías</h3>
                  <div className="tech-tags">
                    {selectedTask.tech.split(',').map((t, i) => (
                      <span key={i} className="tech-tag">{t.trim()}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedTask.time && (
                <div className="modal-section">
                  <h3>⏱️ Tiempo Estimado</h3>
                  <p>{selectedTask.time}</p>
                </div>
              )}

              {/* Demo Notice */}
              <div style={{
                background: 'rgba(224, 133, 99, 0.1)',
                border: '1px solid rgba(224, 133, 99, 0.3)',
                borderRadius: '10px',
                padding: '16px',
                marginTop: '20px',
                textAlign: 'center'
              }}>
                <p style={{ margin: 0, color: 'var(--marca-200)', fontSize: '0.9rem' }}>
                  💡 <strong>Esto es una demo.</strong> Crea tu cuenta para tener tu propio proyecto con datos persistentes.
                </p>
              </div>
            </div>

            <div className="modal-actions">
              <Link to="/register" className="btn btn-primary" onClick={closeModal}>
                🚀 Crear mi proyecto
              </Link>
              <button
                className="btn btn-secondary"
                onClick={(e) => {
                  changeTaskStatus(e, selectedTask.phaseIndex, selectedTask.id);
                  const updatedTask = phases[selectedTask.phaseIndex].tasks.find(t => t.id === selectedTask.id);
                  if (updatedTask) {
                    setSelectedTask({ ...updatedTask, phaseIndex: selectedTask.phaseIndex });
                  }
                }}
              >
                Cambiar Estado
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoRoadmap;
